import { CURRENT_STRATEGY, STRATEGY, API_BASE_URL } from "./config";
import { db } from "../firebase/firebase";
import { getAuth } from "firebase/auth";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  setDoc,
  getDoc,
  limit,
  Timestamp,
} from "firebase/firestore";
import { createClient } from "@supabase/supabase-js";
import axios from "axios";

export class Repository {
  constructor() {
    this._apiOutbox = [];
    this._activeRequests = {};
    this._activeSubscriptions = {};
    this._supabaseClient = null;
    this._currentCompanyId = null;
    this._cachedConfig = null;

    this.CENTRAL_COLLECTIONS = ["users", "companies"];
    this.LOCAL_TABLE_SCHEMAS = [];
    this.onProgressUpdate = null;
    this.onInitializationError = null;
  }

  /**
   * Helper to verify if valid local Supabase credentials exist and are non-empty.
   */
  _hasValidLocalConfig() {
    this._refreshConfigCache();
    if (!this._cachedConfig) return false;
    const { url, anonKey } = this._cachedConfig;
    return Boolean(
      url && url.trim() !== "" && anonKey && anonKey.trim() !== "",
    );
  }

  /**
   * Probes the local stack, triggers queue flushing, and kicks off initialization.
   */
  async probeLocalServer() {
    this._refreshConfigCache();
    if (!this._cachedConfig || !this._hasValidLocalConfig()) {
      this._notifyRoadblock(
        "CACHE_CHECK",
        "Workspace profile missing or empty in localStorage configuration. Skipping local sync.",
      );
      return { success: false };
    }

    const targetUrl = this._cachedConfig.url.replace(/\/$/, "");
    this._notifyProgress(5, "Probing secure local network proxy socket...");

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000);
      await fetch(targetUrl, {
        method: "GET",
        mode: "no-cors",
        signal: controller.signal,
        cache: "no-store",
      });
      clearTimeout(timeoutId);
    } catch (rawError) {
      this._notifyRoadblock(
        "PROXY_UNREACHABLE",
        "The secure local proxy domain or Caddy container is down.",
        rawError,
      );
      return { success: false };
    }

    this._notifyProgress(
      15,
      "Proxy alive. Checking Kong upstream router paths...",
    );
    try {
      const response = await axios.get(`${targetUrl}/rest/v1/`, {
        headers: { apikey: this._cachedConfig.anonKey },
        timeout: 10000,
      });
      if (response.status === 200 || response.status === 201) {
        await this.processOfflineQueues();
        return await this._ensureLocalStructureAndSeedFromFirebase();
      }
    } catch (error) {
      if (
        error.response?.status === 200 ||
        error.response?.status === 401 ||
        error.response?.status === 400
      ) {
        await this.processOfflineQueues();
        return await this._ensureLocalStructureAndSeedFromFirebase();
      }
      this._notifyRoadblock(
        "CONTAINER_INITIALIZING",
        "Upstream Docker services are slow, crashing, or returning gateway errors.",
        error,
      );

      return { success: false };
    }
  }

  /**
   * Automatically flushes accumulated offline write queues when connectivity is restored.
   */
  async processOfflineQueues() {
    try {
      const localQueue = JSON.parse(
        localStorage.getItem("local_sync_queue") || "[]",
      );
      if (localQueue.length > 0 && this._hasValidLocalConfig()) {
        const client = this._getLocalSupabaseClient();
        const remainingQueue = [];

        for (const item of localQueue) {
          try {
            if (item.action === "UPSERT") {
              await client
                .from(item.tableName)
                .upsert(item.payload, { onConflict: "id" });
            }
          } catch (err) {
            remainingQueue.push(item);
          }
        }
        localStorage.setItem(
          "local_sync_queue",
          JSON.stringify(remainingQueue),
        );
      }
    } catch (e) {
      console.error("Failed to process local sync queue", e);
    }

    try {
      const cloudQueue = JSON.parse(
        localStorage.getItem("cloud_sync_outbox") || "[]",
      );
      if (cloudQueue.length > 0) {
        const remainingCloudQueue = [];

        for (const item of cloudQueue) {
          try {
            if (item.action === "UPSERT") {
              const docRef = doc(db, item.collectionName, item.id);
              await setDoc(docRef, item.data, { merge: true });
            }
          } catch (err) {
            remainingCloudQueue.push(item);
          }
        }
        localStorage.setItem(
          "cloud_sync_outbox",
          JSON.stringify(remainingCloudQueue),
        );
      }
    } catch (e) {
      console.error("Failed to process cloud sync outbox", e);
    }
  }

  async _ensureLocalStructureAndSeedFromFirebase() {
    const client = this._getLocalSupabaseClient();
    const targetCompanyId = this._cachedConfig.companyId;

    this._notifyProgress(30, "Scanning cloud metadata footprint blueprints...");
    await this._discoverLocalTableSchemasFromFirebase(targetCompanyId);

    const totalTables = this.LOCAL_TABLE_SCHEMAS.length;
    if (totalTables === 0) {
      this._notifyProgress(
        100,
        "Initialization complete. Zero tenant tables required.",
      );
      return { success: true };
    }

    const progressStart = 30;
    const progressRange = 70;
    let tablesProcessed = 0;

    for (const tableName of this.LOCAL_TABLE_SCHEMAS) {
      const currentTablePercentage =
        progressStart +
        Math.floor((tablesProcessed / totalTables) * progressRange);
      this._notifyProgress(
        currentTablePercentage,
        `Evaluating baseline state for: "${tableName}"...`,
      );
      let tableExists = false;

      try {
        const { data: latestRecord, error: checkError } = await client
          .from(tableName)
          .select("updated_at")
          .order("updated_at", { ascending: false })
          .limit(1);

        if (checkError) throw checkError;
        tableExists = true;
      } catch (err) {
        tableExists = false;
        console.warn(
          `[INITIALIZER] Table "${tableName}" structural check failed. Attempting creation...`,
        );
      }

      if (!tableExists) {
        this._notifyProgress(
          currentTablePercentage + 2,
          `Compiling missing relations for "${tableName}"...`,
        );
        const ddlSuccess = await this._createTableStructureViaRPC(tableName);
        if (!ddlSuccess) {
          console.warn(
            `[DDL WARNING] Could not auto-create "${tableName}" via RPC. Continuing with data check...`,
          );
        }
      }

      this._notifyProgress(
        currentTablePercentage + 4,
        `Verifying data integrity for "${tableName}"...`,
      );
      try {
        const { count, error: countError } = await client
          .from(tableName)
          .select("*", { count: "exact", head: true });

        if (countError || count === 0 || !tableExists) {
          console.log(
            `[SYNC] Table "${tableName}" is empty or missing. Seeding from Firebase...`,
          );
          await this._bootstrapDataFromFirebaseForTable(
            tableName,
            targetCompanyId,
          );
        } else {
          console.log(
            `[SYNC] Table "${tableName}" has ${count} records. Skipping Firebase seed.`,
          );
        }
      } catch (seedErr) {
        console.error(
          `[INITIALIZER WARNING] Data sync partial failure for "${tableName}":`,
          seedErr,
        );
      }

      tablesProcessed++;
    }

    this._notifyProgress(100, "Local database environment optimized!");
    return { success: true };
  }

  async _createTableStructureViaRPC(tableName) {
    this._refreshConfigCache();
    const cleanUrl = this._cachedConfig.url.trim().replace(/\/+$/, "");
    const cleanKey = this._cachedConfig.anonKey.trim();

    const sqlStatement = `
      CREATE TABLE IF NOT EXISTS public."${tableName}" (
        id text NOT NULL PRIMARY KEY,
        "companyId" text,
        name text,
        details jsonb,
        updated_at timestamp with time zone DEFAULT timezone('utc'::text, now())
      );
      ALTER TABLE public."${tableName}" ENABLE ROW LEVEL SECURITY;
      DROP POLICY IF EXISTS "Allow local dev access" ON public."${tableName}";
      CREATE POLICY "Allow local dev access" ON public."${tableName}" FOR ALL USING (true);
      NOTIFY pgrst, 'reload schema';
    `.trim();

    try {
      const response = await fetch(`${cleanUrl}/rest/v1/rpc/exec_sql`, {
        method: "POST",
        headers: {
          apikey: cleanKey,
          Authorization: `Bearer ${cleanKey}`,
          "Content-Type": "application/json",
          Prefer: "params=single-object",
        },
        body: JSON.stringify({ sql: sqlStatement }),
      });

      if (!response.ok) return false;
      return true;
    } catch (sqlErr) {
      console.warn(
        `[RPC DDL NOTICE] exec_sql endpoint unavailable on self-hosted Docker for "${tableName}".`,
      );
      return false;
    }
  }

  async _bootstrapDataFromFirebaseForTable(tableName, companyId) {
    try {
      const snap = await getDocs(
        query(collection(db, tableName), where("companyId", "==", companyId)),
      );
      if (snap.empty) return;

      const client = this._getLocalSupabaseClient();

      for (const docSnapshot of snap.docs) {
        const data = docSnapshot.data();
        const payload = {
          id: docSnapshot.id,
          companyId: data.companyId || companyId,
          name: data.name || tableName,
          details: data,
          updated_at: new Date().toISOString(),
        };
        const { error } = await client
          .from(tableName)
          .upsert(payload, { onConflict: "id" });

        if (error) {
          console.error(`[SYNC ERROR] Failed on ID: ${docSnapshot.id}`, error);
        }
      }
    } catch (err) {
      console.error(
        `[SYNC ENGINE] Error during table sync for ${tableName}:`,
        err,
      );
    }
  }

  async upsertData(collectionName, id, partialData) {
    const jsIsoString = new Date().toISOString();
    const processedDetails = {};
    Object.keys(partialData).forEach((key) => {
      processedDetails[key] = partialData[key];
    });

    const localPayload = {
      id,
      companyId:
        partialData.companyId || this._cachedConfig?.companyId || "default",
      name: partialData.name || collectionName,
      details: processedDetails,
      updated_at: jsIsoString,
    };

    if (
      this.CENTRAL_COLLECTIONS.includes(collectionName) ||
      !this._hasValidLocalConfig()
    ) {
      await this._upsertDataCloud(collectionName, id, partialData);
      return { ...partialData, id };
    }

    await this._upsertDataLocalSupabase(collectionName, id, localPayload);
    const cloudPayload = {
      ...partialData,
      updated_at: Timestamp.fromDate(new Date(jsIsoString)),
    };
    this._queueCloudBackupSync(collectionName, id, cloudPayload);
    return { ...partialData, id };
  }

  async _upsertDataLocalSupabase(tableName, id, payload) {
    try {
      const client = this._getLocalSupabaseClient();
      const { error } = await client
        .from(tableName)
        .upsert(payload, { onConflict: "id" });
      if (error) throw error;
    } catch (err) {
      console.error(
        `[LOCAL UPSERT FAULT] Local storage write failed for "${tableName}". Queuing to local_sync_queue...`,
        err.message,
      );
      this._persistToStorageQueue("local_sync_queue", {
        action: "UPSERT",
        tableName,
        id,
        payload,
        timestamp: Date.now(),
      });
    }
  }

  _persistToStorageQueue(queueName, item) {
    try {
      const queue = JSON.parse(localStorage.getItem(queueName) || "[]");
      queue.push(item);
      localStorage.setItem(queueName, JSON.stringify(queue));
    } catch (storageErr) {
      console.error(`Failed to persist item to ${queueName}:`, storageErr);
    }
  }

  async _discoverLocalTableSchemasFromFirebase(companyId) {
    try {
      if (this.LOCAL_TABLE_SCHEMAS.length > 0) return this.LOCAL_TABLE_SCHEMAS;
      const theoreticalCollections = [
        "assets",
        "categories",
        "subCategories",
        "brands",
        "vendors",
        "locations",
        "departments",
        "auditlogs",
        "auditeventhistory",
        "auditevents",
        "auditsupporttickets",
        "helpdeskissues",
        "preferences",
        "publicAssetTokens",
        "warrantyReminders",
      ];
      const detectedCollections = [];

      for (const colName of theoreticalCollections) {
        if (this.CENTRAL_COLLECTIONS.includes(colName)) continue;
        const q = query(
          collection(db, colName),
          where("companyId", "==", companyId),
          limit(1),
        );
        const snap = await getDocs(q);
        if (!snap.empty) detectedCollections.push(colName);
      }
      this.LOCAL_TABLE_SCHEMAS =
        detectedCollections.length > 0
          ? detectedCollections
          : theoreticalCollections;
      return this.LOCAL_TABLE_SCHEMAS;
    } catch (err) {
      return [
        "assets",
        "categories",
        "subCategories",
        "brands",
        "vendors",
        "locations",
        "departments",
      ];
    }
  }

  _refreshConfigCache() {
    try {
      const companyDataRaw = localStorage.getItem("companyData");
      if (!companyDataRaw) {
        this._cachedConfig = null;
        this._supabaseClient = null;
        return;
      }
      const companyData = JSON.parse(companyDataRaw);
      const newUrl = companyData.localSupabaseURL;
      const newKey = companyData.localSupabaseAnonKey;

      if (
        this._cachedConfig &&
        (this._cachedConfig.url !== newUrl ||
          this._cachedConfig.anonKey !== newKey)
      ) {
        this._supabaseClient = null;
      }

      this._cachedConfig = {
        url: newUrl,
        anonKey: newKey,
        companyId: companyData.id || "default",
      };
    } catch (e) {
      this._cachedConfig = null;
      this._supabaseClient = null;
    }
  }

  _getLocalSupabaseClient() {
    if (!this._supabaseClient) {
      this._refreshConfigCache();
      if (!this._cachedConfig?.url || !this._cachedConfig?.anonKey) {
        throw new Error(
          "[SYNC ENGINE] Supabase config is missing URL or anonKey.",
        );
      }

      const cleanUrl = this._cachedConfig.url.trim().replace(/\/+$/, "");
      const cleanKey = this._cachedConfig.anonKey.trim();

      this._supabaseClient = createClient(cleanUrl, cleanKey, {
        db: {
          schema: "public",
        },
        auth: {
          persistSession: false,
          autoRefreshToken: false,
          detectSessionInUrl: false,
        },
      });
    }
    return this._supabaseClient;
  }

  async getAuthHeaders() {
    const user = getAuth().currentUser;
    if (!user) throw new Error("Unauthorized");
    const token = await user.getIdToken();
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  }

  _getQueryKey(collectionName, filters = [], sortRules = [], limitVal = null) {
    const sortedFilters = [...filters].sort((a, b) =>
      a.field.localeCompare(b.field),
    );
    const sortedSorts = [...sortRules].sort((a, b) =>
      a.field.localeCompare(b.field),
    );
    let key = `${collectionName}-f:${JSON.stringify(
      sortedFilters,
    )}-s:${JSON.stringify(sortedSorts)}`;
    if (limitVal) key += `-l:${limitVal}`;
    return key;
  }

  async fetchById(collectionName, id) {
    const requestKey = `id-${collectionName}-${id}`;
    if (this._activeRequests[requestKey])
      return this._activeRequests[requestKey];

    this._activeRequests[requestKey] = (async () => {
      if (
        this.CENTRAL_COLLECTIONS.includes(collectionName) ||
        !this._hasValidLocalConfig()
      ) {
        return this._fetchByIdCloud(collectionName, id);
      }
      return await this._fetchByIdLocalSupabase(collectionName, id);
    })();
    try {
      return await this._activeRequests[requestKey];
    } finally {
      delete this._activeRequests[requestKey];
    }
  }

  async fetchData(
    collectionName,
    filters = [],
    sortRules = [],
    limitVal = null,
  ) {
    const lockKey = this._getQueryKey(
      collectionName,
      filters,
      sortRules,
      limitVal,
    );
    if (this._activeRequests[lockKey]) return this._activeRequests[lockKey];

    this._activeRequests[lockKey] = (async () => {
      if (!this._hasValidLocalConfig()) {
        return await this._fetchDataCloud(
          collectionName,
          filters,
          sortRules,
          limitVal,
        );
      }

      try {
        const data = await this._fetchDataLocalSupabase(
          collectionName,
          filters,
          sortRules,
          limitVal,
        );

        if (!data || data.length === 0) {
          console.warn(
            `[SYNC WARNING] Local DB empty for "${collectionName}". Triggering auto-sync...`,
          );
          await this._bootstrapDataFromFirebaseForTable(
            collectionName,
            this._cachedConfig.companyId,
          );
          return await this._fetchDataLocalSupabase(
            collectionName,
            filters,
            sortRules,
            limitVal,
          );
        }
        return data;
      } catch (supabaseError) {
        console.error(
          `[LOCAL DB ERROR] Query failed for ${collectionName}:`,
          supabaseError,
        );
        return await this._fetchDataFromFirebaseBackupFallback(
          collectionName,
          filters,
          sortRules,
          limitVal,
        );
      }
    })();

    try {
      return await this._activeRequests[lockKey];
    } finally {
      delete this._activeRequests[lockKey];
    }
  }

  async refreshData(
    collectionName,
    filters = [],
    sortRules = [],
    limitVal = null,
  ) {
    return this.fetchData(collectionName, filters, sortRules, limitVal);
  }

  async subscribeToUpdates(
    collectionName,
    filters = [],
    sortRules = [],
    limitVal = null,
    callback,
  ) {
    if (typeof sortRules === "function") {
      callback = sortRules;
      sortRules = [];
      limitVal = null;
    }
    const subKey = this._getQueryKey(
      collectionName,
      filters,
      sortRules,
      limitVal,
    );
    if (this._activeSubscriptions[subKey]) {
      const existingSub = this._activeSubscriptions[subKey];
      existingSub.callbacks.add(callback);
      this.fetchData(collectionName, filters, sortRules, limitVal).then(
        (data) => {
          if (existingSub.callbacks.has(callback)) callback(data);
        },
      );
      return () => this._unsubscribe(subKey, callback);
    }

    const subscriptionRecord = {
      callbacks: new Set([callback]),
      nativeUnsubscribe: null,
      isCancelled: false,
    };
    this._activeSubscriptions[subKey] = subscriptionRecord;

    if (
      this.CENTRAL_COLLECTIONS.includes(collectionName) ||
      !this._hasValidLocalConfig()
    ) {
      if (CURRENT_STRATEGY === STRATEGY.NATIVE) {
        const queryConstraints = [
          ...filters.map((f) => where(f.field, "==", f.value)),
          ...sortRules.map((s) => orderBy(s.field, s.order)),
        ];
        if (limitVal) queryConstraints.push(limit(limitVal));

        const q = query(collection(db, collectionName), ...queryConstraints);
        subscriptionRecord.nativeUnsubscribe = onSnapshot(q, (snapshot) => {
          const streamDocs = snapshot.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          }));
          subscriptionRecord.callbacks.forEach((cb) => cb(streamDocs));
        });
      }
    } else {
      try {
        const client = this._getLocalSupabaseClient();
        const channel = client
          .channel(`public:${collectionName}:${subKey}`)
          .on(
            "postgres_changes",
            { event: "*", schema: "public", table: collectionName },
            () => {
              this.fetchData(collectionName, filters, sortRules, limitVal).then(
                (freshData) => {
                  if (!subscriptionRecord.isCancelled)
                    subscriptionRecord.callbacks.forEach((cb) => cb(freshData));
                },
              );
            },
          )
          .subscribe();
        subscriptionRecord.nativeUnsubscribe = () => {
          subscriptionRecord.isCancelled = true;
          try {
            client.removeChannel(channel);
          } catch (err) {}
        };
      } catch (err) {}

      this.fetchData(collectionName, filters, sortRules, limitVal).then(
        (initData) => {
          if (!subscriptionRecord.isCancelled)
            subscriptionRecord.callbacks.forEach((cb) => cb(initData));
        },
      );
    }
    return () => this._unsubscribe(subKey, callback);
  }

  _unsubscribe(subKey, callback) {
    const record = this._activeSubscriptions[subKey];
    if (!record) return;
    record.callbacks.delete(callback);
    if (record.callbacks.size === 0) {
      if (typeof record.nativeUnsubscribe === "function")
        record.nativeUnsubscribe();
      delete this._activeSubscriptions[subKey];
    }
  }

  async deleteData(collectionName, id) {
    if (
      this.CENTRAL_COLLECTIONS.includes(collectionName) ||
      !this._hasValidLocalConfig()
    ) {
      if (CURRENT_STRATEGY === STRATEGY.NATIVE) {
        await setDoc(
          doc(db, collectionName, id),
          { deleted: true },
          { merge: true },
        );
      }
    } else {
      try {
        const client = this._getLocalSupabaseClient();
        await client.from(collectionName).delete().eq("id", id);
      } catch (err) {}
      this._queueCloudBackupSync(collectionName, id, {
        deleted: true,
        updated_at: Timestamp.now(),
      });
    }
    return { success: true };
  }

  async _fetchByIdLocalSupabase(tableName, id) {
    try {
      const client = this._getLocalSupabaseClient();
      const { data, error } = await client
        .from(tableName)
        .select("*")
        .eq("id", id)
        .single();
      if (error) {
        if (error.code === "PGRST116") return null;
        throw error;
      }
      return {
        id: data.id,
        companyId: data.companyId,
        name: data.name,
        updated_at: data.updated_at,
        ...(data.details || {}),
      };
    } catch (supabaseError) {
      return typeof this._fetchByIdNative === "function"
        ? await this._fetchByIdNative(tableName, id)
        : null;
    }
  }

  async _fetchDataLocalSupabase(tableName, filters, sortRules, limitVal) {
    const client = this._getLocalSupabaseClient();
    let queryBuilder = client
      .from(tableName)
      .select("id, companyId, name, details, updated_at");

    filters.forEach((f) => {
      queryBuilder = queryBuilder.filter(`details->>${f.field}`, "eq", f.value);
    });

    sortRules.forEach((s) => {
      queryBuilder = queryBuilder.order(s.field, {
        ascending: s.order === "asc",
      });
    });

    if (limitVal) queryBuilder = queryBuilder.limit(limitVal);

    const { data, error } = await queryBuilder;
    if (error) throw error;

    return data.map((item) => {
      const { details, ...rest } = item;
      return {
        ...rest,
        ...(details || {}),
      };
    });
  }

  async _fetchDataFromFirebaseBackupFallback(
    tableName,
    filters,
    sortRules,
    limitVal,
  ) {
    let firestoreQuery = collection(db, tableName);
    const queryConstraints = [];
    filters.forEach((f) => {
      if (f.value !== undefined && f.value !== null)
        queryConstraints.push(where(f.field, "==", f.value));
    });
    sortRules.forEach((s) => {
      queryConstraints.push(orderBy(s.field, s.order));
    });
    if (limitVal) queryConstraints.push(limit(limitVal));

    const q = query(firestoreQuery, ...queryConstraints);
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  async _fetchByIdCloud(collectionName, id) {
    return await this._fetchByIdNative(collectionName, id);
  }

  async _fetchDataCloud(collectionName, filters, sortRules, limitVal) {
    return await this._fetchDataNative(
      collectionName,
      filters,
      sortRules,
      limitVal,
    );
  }

  async _upsertDataCloud(collectionName, id, partialData) {
    await setDoc(doc(db, collectionName, id), partialData, { merge: true });
  }

  async _queueCloudBackupSync(collectionName, id, partialData) {
    try {
      const docRef = doc(db, collectionName, id);
      await setDoc(docRef, partialData, { merge: true });
    } catch (err) {
      console.warn(
        `[CLOUD SYNC FAULT] Cloud write failed. Queuing to cloud_sync_outbox...`,
        err.message,
      );
      this._apiOutbox.push({
        action: "UPSERT",
        collectionName,
        id,
        data: partialData,
      });
      this._persistToStorageQueue("cloud_sync_outbox", {
        action: "UPSERT",
        collectionName,
        id,
        data: partialData,
        timestamp: Date.now(),
      });
    }
  }

  async _fetchByIdNative(collectionName, id) {
    const snap = await getDoc(doc(db, collectionName, id));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  }

  async _fetchDataNative(collectionName, filters, sortRules, limitVal) {
    const queryConstraints = [
      ...filters.map((f) => where(f.field, "==", f.value)),
      ...sortRules.map((s) => orderBy(s.field, s.order)),
    ];
    if (limitVal) queryConstraints.push(limit(limitVal));
    const q = query(collection(db, collectionName), ...queryConstraints);
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  async uploadFile(file, folder = "uploads") {
    return await this._uploadFileNative(file, folder);
  }

  async _uploadFileNative(file, folder) {
    const { getStorage, ref, uploadBytes, getDownloadURL } =
      await import("firebase/storage");
    const storage = getStorage();
    const storageRef = ref(storage, `${folder}/${file.name}_${Date.now()}`);
    const snapshot = await uploadBytes(storageRef, file);
    return await getDownloadURL(snapshot.ref);
  }

  _notifyProgress(percentage, currentAction) {
    if (typeof this.onProgressUpdate === "function")
      this.onProgressUpdate(
        Math.min(100, Math.max(0, percentage)),
        currentAction,
      );
  }

  _notifyRoadblock(phase, message, rawError = null) {
    const errorReport = {
      phase,
      message,
      code: rawError?.code || rawError?.response?.status || "N/A",
      details: rawError?.message || JSON.stringify(rawError) || "None",
      timestamp: new Date().toISOString(),
    };
    if (typeof this.onInitializationError === "function")
      this.onInitializationError(errorReport);
  }

  /**
   * ============================================================
   * ADMIN FORCE RECONCILE & HEAL (MISSING RECORDS ONLY)
   * ============================================================
   */
  async adminForceReconcileAndHeal(tableName, companyId) {
    const currentUser = JSON.parse(localStorage.getItem("userData") || "{}");
    const allowedRoles = ["admin", "company-admin", "system-admin"];

    if (!currentUser?.role || !allowedRoles.includes(currentUser.role)) {
      throw new Error(
        "Unauthorized: Administrative privileges are required to perform data reconciliation.",
      );
    }

    const client = this._getLocalSupabaseClient();

    this._notifyProgress(
      10,
      `[ADMIN HEAL] Reading local records for "${tableName}"...`,
    );

    const { data: localRows, error: localError } = await client
      .from(tableName)
      .select("*")
      .eq("companyId", companyId);

    if (localError) {
      throw localError;
    }

    this._notifyProgress(
      30,
      `[ADMIN HEAL] Reading Firebase records for "${tableName}"...`,
    );

    const firebaseQuery = query(
      collection(db, tableName),
      where("companyId", "==", companyId),
    );

    const snapshot = await getDocs(firebaseQuery);

    const cloudRows = snapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    }));

    const localMap = new Map(
      (localRows || []).map((record) => [String(record.id), record]),
    );

    const cloudMap = new Map(
      cloudRows.map((record) => [String(record.id), record]),
    );

    const timestampToISO = (value) => {
      if (!value) {
        return null;
      }
      if (typeof value?.toDate === "function") {
        return value.toDate().toISOString();
      }
      if (value instanceof Date) {
        return value.toISOString();
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return null;
      }
      return date.toISOString();
    };

    const missingLocally = [];
    const missingInCloud = [];

    for (const [id, cloudRecord] of cloudMap) {
      if (!localMap.has(id)) {
        missingLocally.push(cloudRecord);
      }
    }

    for (const [id, localRecord] of localMap) {
      if (!cloudMap.has(id)) {
        missingInCloud.push(localRecord);
      }
    }

    const report = {
      tableName,
      companyId,
      localRecordsRead: localMap.size,
      firebaseRecordsRead: cloudMap.size,
      firebaseToLocalMissing: missingLocally.length,
      localToFirebaseMissing: missingInCloud.length,
      totalChanges: missingLocally.length + missingInCloud.length,
      inSync: missingLocally.length === 0 && missingInCloud.length === 0,
    };

    if (report.inSync) {
      this._notifyProgress(
        100,
        `[ADMIN HEAL] "${tableName}" has no missing records across platforms.`,
      );
      return report;
    }

    this._notifyProgress(
      60,
      `[ADMIN HEAL] Backfilling ${report.totalChanges} missing records...`,
    );

    await this._executeHealBatch(
      tableName,
      companyId,
      missingLocally,
      missingInCloud,
      timestampToISO,
    );

    localStorage.removeItem("sync_anomalies");

    this._notifyProgress(
      100,
      `[ADMIN HEAL] "${tableName}" missing records healed successfully.`,
    );

    return report;
  }

  async _executeHealBatch(tableName, companyId, updateLocally, updateInCloud) {
    const client = this._getLocalSupabaseClient();

    for (const cloudRecord of updateLocally) {
      let isoTimestamp = new Date().toISOString();
      if (cloudRecord.updated_at?.toDate) {
        isoTimestamp = cloudRecord.updated_at.toDate().toISOString();
      } else if (cloudRecord.updated_at) {
        isoTimestamp = new Date(cloudRecord.updated_at).toISOString();
      }

      const payload = {
        id: cloudRecord.id,
        companyId: cloudRecord.companyId || companyId,
        name: cloudRecord.name || tableName,
        details: cloudRecord,
        updated_at: isoTimestamp,
      };

      await client.from(tableName).upsert(payload, { onConflict: "id" });
    }

    for (const localRecord of updateInCloud) {
      const dateVal = localRecord.updated_at
        ? new Date(localRecord.updated_at)
        : new Date();

      const cloudPayload = {
        ...(localRecord.details || {}),
        id: localRecord.id,
        companyId: localRecord.companyId || companyId,
        updated_at: Timestamp.fromDate(dateVal),
      };

      await setDoc(doc(db, tableName, localRecord.id), cloudPayload, {
        merge: true,
      });
    }
  }
}

const repo = new Repository();
export { repo };
